import { useEffect, useRef } from 'react'

// Defining the useEventListener hook
export function useEventListener<K extends keyof WindowEventMap>(
  eventName: K,
  handler: (event: WindowEventMap[K]) => void,
  element: Window | Document | HTMLElement | null = window,
  disableWhen: boolean = false,
) {
  // Create a ref that stores the handler
  const savedHandler = useRef<((event: WindowEventMap[K]) => void) | undefined>(
    undefined,
  )

  useEffect(() => {
    // Update the ref to the current handler on each render
    savedHandler.current = handler
  }, [handler])

  useEffect(() => {
    if (!element) return
    // Define the event listener that wraps the handler from the ref
    const eventListener: typeof handler = (event) => {
      // Execute the saved handler, if it exists
      if (savedHandler.current) {
        savedHandler.current(event)
      }
    }

    // Add the event listener
    if (!disableWhen) {
      // @ts-expect-error
      element.addEventListener(eventName, eventListener)
    } else {
      // @ts-expect-error
      element.removeEventListener(eventName, eventListener)
    }

    // Return a cleanup function that will remove the event listener
    return () => {
      // @ts-expect-error
      element.removeEventListener(eventName, eventListener)
    }
  }, [eventName, element, disableWhen]) // Re-run if eventName or element changes
}
